import type { AppState } from '@auth0/auth0-react';

import { useAuth0, Auth0Provider } from '@auth0/auth0-react';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';

import { storeEventsTrialToken } from 'src/utils/eventsTrial';

import { getConfig } from 'src/global-config';

import { useStoreLocalStorage } from 'src/auth/hooks/use-store-localStorage';

import { AuthContext } from '../auth-context';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const CONFIG = getConfig();

  const { domain, clientId, callbackUrl } = CONFIG.auth0;

  const { baseUrl } = CONFIG.develop;

  const onRedirectCallback = useCallback((appState?: AppState) => {
    if (appState?.eventsTrialToken) {
      storeEventsTrialToken(appState.eventsTrialToken as string);
    }
    window.location.replace(appState?.returnTo || window.location.pathname);
  }, []);

  if (!(domain && clientId && callbackUrl)) {
    return null;
  }

  // An ATS (Greenhouse) returns the browser to /integrations/callback?code=…&state=… — the same
  // query shape as an Auth0 login return. Left alone, the SDK would try to finish an Auth0 login
  // with the ATS's code, fail, report the user as signed out, and the sign-in page would send them
  // straight back into the same loop. Those parameters belong to the integrations page, not Auth0.
  const isIntegrationsReturn = window.location.pathname === paths.integrations.callback;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: callbackUrl,
        scope: 'openid profile email offline_access',
        audience: baseUrl,
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      skipRedirectCallback={isIntegrationsReturn}
    >
      <AuthProviderContainer>{children}</AuthProviderContainer>
    </Auth0Provider>
  );
}

// ----------------------------------------------------------------------

function AuthProviderContainer({ children }: Props) {
  const { user, isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const { saveValue: saveAuth0AccessToken } = useStoreLocalStorage('auth0AccessToken', '');

  const [accessToken, setAccessToken] = useState<string | null>(null);

  const getAccessToken = useCallback(async () => {
    try {
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();


        setAccessToken(token);
        saveAuth0AccessToken(token);
      } else {
        setAccessToken(null);
        saveAuth0AccessToken('');
      }
    } catch (error) {
      console.error(error);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    getAccessToken();
  }, [getAccessToken]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = isAuthenticated ? 'authenticated' : 'unauthenticated';

  const status = isLoading ? 'loading' : checkAuthenticated;

  //TODO: Clean Values Here
  const memoizedValue = useMemo(
    () => ({
      user: user
        ? {
            ...user,
            id: user?.sub,
            accessToken,
            displayName: user?.name,
            photoURL: user?.picture,
            role: user?.role ?? 'admin',
          }
        : null,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      auth0AccessToken: accessToken,
    }),
    [accessToken, status, user]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
